import React, { useCallback, useEffect, useState } from "react";
import { MAX_MISTAKES, PROPOSAL_WORDS } from "../../lib/constants";
import { PuzzleDataContext } from "../PuzzleDataProvider";
import {
  loadGameStateFromLocalStorage,
  saveGameStateToLocalStorage,
} from "../../lib/local-storage";
import {
  isGameDataEquivalent,
  isGuessesFromGame,
} from "../../lib/game-helpers";

export const GameStatusContext = React.createContext();

function GameStatusProvider({ children }) {
  const { gameData, setGameData } = React.useContext(PuzzleDataContext);
  const [submittedGuesses, setSubmittedGuesses] = React.useState([]);
  const [solvedGameData, setSolvedGameData] = React.useState(() => {
    const loadedState = loadGameStateFromLocalStorage();
    if (!isGameDataEquivalent({ gd1: gameData, gd2: loadedState?.gameData })) {
      return [];
    }
    if (
      !isGuessesFromGame({
        gameData,
        submittedGuesses: loadedState?.submittedGuesses,
      })
    ) {
      return [];
    }
    if (Array.isArray(loadedState?.submittedGuesses)) {
      setSubmittedGuesses(loadedState.submittedGuesses);
    }
    return loadedState?.solvedGameData || [];
  });

  const [isGameOver, setIsGameOver] = useState(false);
  const [isGameWon, setIsGameWon] = useState(false);
  const [guessCandidate, setGuessCandidate] = useState([]);
  const [isProposalPhase, setIsProposalPhase] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [remainingWords, setRemainingWords] = useState([]);

  const numMistakesUsed = submittedGuesses.length - solvedGameData.length;

  // Check if we should trigger the proposal animation
  useEffect(() => {
    const remainingCategories = gameData.filter(
      (category) => !solvedGameData.some((solved) => solved.category === category.category)
    );

    // If only one category remains and we haven't started the proposal phase yet
    if (remainingCategories.length === 1 && !isProposalPhase && solvedGameData.length > 0) {
      const remainingCategory = remainingCategories[0];
      
      // Store the original words in the order they appear in the category
      setRemainingWords([...remainingCategory.words]);
      setIsProposalPhase(true);
      setIsAnimating(true);

      // After animation completes, update the game data with proposal words
      // Increased duration to account for slower animations
      const animationDuration = remainingCategory.words.length * 1000 + 1000;
      const timer = setTimeout(() => {
        const updatedGameData = gameData.map((cat) => {
          if (cat.category === remainingCategory.category) {
            // Map each word to its corresponding proposal word based on position
            const newWords = remainingCategory.words.map((_, index) => 
              PROPOSAL_WORDS[index % PROPOSAL_WORDS.length] || ''
            );
            
            return {
              ...cat,
              words: newWords,
            };
          }
          return cat;
        });
        
        setGameData(updatedGameData);
        setIsAnimating(false);
      }, animationDuration);

      return () => clearTimeout(timer);
    }
  }, [solvedGameData, gameData, isProposalPhase, setGameData]);

  // Check if game is won
  useEffect(() => {
    if (solvedGameData.length === gameData.length) {
      setIsGameOver(true);
      setIsGameWon(true);
    }
    const gameState = { submittedGuesses, solvedGameData, gameData };
    saveGameStateToLocalStorage(gameState);
  }, [solvedGameData, gameData, submittedGuesses]);

  // Check if all mistakes have been used
  useEffect(() => {
    if (numMistakesUsed >= MAX_MISTAKES) {
      setIsGameOver(true);
      setIsGameWon(false);
    }
    const gameState = { submittedGuesses, solvedGameData, gameData };
    saveGameStateToLocalStorage(gameState);
  }, [submittedGuesses, numMistakesUsed, solvedGameData, gameData]);

  // Get the proposal word for a given position
  const getProposalWord = useCallback((position) => {
    return position >= 0 && position < PROPOSAL_WORDS.length ? PROPOSAL_WORDS[position] : null;
  }, []);

  return (
    <GameStatusContext.Provider
      value={{
        isGameOver,
        isGameWon,
        numMistakesUsed,
        solvedGameData,
        setSolvedGameData,
        submittedGuesses,
        setSubmittedGuesses,
        guessCandidate,
        setGuessCandidate,
        isProposalPhase,
        isAnimating,
        getProposalWord,
      }}
    >
      {children}
    </GameStatusContext.Provider>
  );
}

export default GameStatusProvider;
